import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG } from '../config/api';
import type { ApiError } from '../types/api.types';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add JWT token
    this.api.interceptors.request.use(
      (config) => {
        // Always try to get the latest token from the instance or localStorage
        const currentToken = this.token || localStorage.getItem(API_CONFIG.tokenKey);
        if (currentToken) {
          // 1. Add to Authorization header
          config.headers.Authorization = `Bearer ${currentToken}`;
          
          // 2. Add to query parameters as fallback (in case server strips Authorization header)
          config.params = {
            ...config.params,
            token: currentToken
          };
          
          // Sync internal state if it was only in localStorage
          if (!this.token) this.token = currentToken;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          // Token expired or invalid (non-critical for public access)
          this.clearToken();
          console.warn('Authentication warning:', error.response?.data);
        }
        return Promise.reject(this.formatError(error));
      }
    );

    // Load token from localStorage on init
    this.loadToken();
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem(API_CONFIG.tokenKey, token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem(API_CONFIG.tokenKey);
  }

  private loadToken() {
    const savedToken = localStorage.getItem(API_CONFIG.tokenKey);
    if (savedToken) {
      this.token = savedToken;
    }
  }

  private formatError(error: AxiosError<ApiError>): ApiError {
    if (error.response?.data) {
      return error.response.data;
    }
    
    return {
      code: 'network_error',
      message: error.message || 'خطا در برقراری ارتباط با سرور',
      data: {
        status: error.response?.status || 0,
      },
    };
  }

  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.api.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.api.delete<T>(url);
    return response.data;
  }
}

export const apiService = new ApiService();
